import { readonly, ref, useContext } from "@nuxtjs/composition-api";
import { findItemOnWishlist } from "../../helpers/findItemOnWishlist";
import { Logger } from "@wemade-vsf/core";
import { useWishlistStore } from "../../store/wishlistStore";
import { useGuestWishlist } from "../useGuestWishlist";
import { useUiNotification } from "@wemade-vsf/composables";
import { useEvent } from "@wemade-vsf/composables";
function useWishlist() {
  var _a;
  const wishlistStore = useWishlistStore();
  const { app, $config } = useContext();
  const { send: sendNotification } = useUiNotification();
  const { load: loadGuestWishlist, loadItemsCount: loadGuestWishlistItemsCount } = useGuestWishlist();
  const loading = ref(false);
  const calculateWishlistTotal = (wishlists) => wishlists.reduce((acc, current) => {
    var _a2;
    return acc + ((_a2 = current == null ? void 0 : current.items_count) != null ? _a2 : 0);
  }, 0);
  const error = ref({
    addItem: null,
    removeItem: null,
    load: null,
    clear: null,
    loadItemsCount: null,
    afterAddingWishlistItemToCart: null
  });
  const enabled = ((_a = $config.wishlist) == null ? void 0 : _a.enable) ? true : false;
  const load = async (params) => {
    var _a2, _b, _c, _d;
    Logger.debug("useWishlist/load");
    try {
      loading.value = true;
      Logger.debug("[Magento Storefront]: useWishlist.load params->", params);
      const apiState = app.$vsf.$magento.config.state;
      if (apiState.getCustomerToken()) {
        const { data } = await app.$vsf.$magento.api.wishlist(params == null ? void 0 : params.searchParams, (_a2 = params == null ? void 0 : params.customQuery) != null ? _a2 : null, params == null ? void 0 : params.customHeaders);
        Logger.debug("[Result]:", { data });
        const loadedWishlist = (_c = (_b = data == null ? void 0 : data.customer) == null ? void 0 : _b.wishlists) != null ? _c : [];
        if (loadedWishlist[0]) {
          wishlistStore.wishlist = loadedWishlist[0];
        }
      } else {
        const loadedWishlist = await loadGuestWishlist((_d = params == null ? void 0 : params.searchParams) != null ? _d : {});
        if (loadedWishlist) {
          wishlistStore.wishlist = loadedWishlist;
        }
      }
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error("useWishlist/load", err);
    } finally {
      loading.value = false;
    }
    return wishlistStore.wishlist;
  };
  const isInWishlist = ({ product }) => {
    Logger.debug("useWishlist/isInWishlist", product);
    const wishlistProduct = findItemOnWishlist(wishlistStore.wishlist, product);
    return !!((wishlistProduct == null ? void 0 : wishlistProduct.id) && (wishlistProduct == null ? void 0 : wishlistProduct.quantity));
  };
  const setWishlist = (newWishlist) => {
    wishlistStore.wishlist = newWishlist;
    Logger.debug("useWishlist/setWishlist", newWishlist);
  };
  const removeItem = async ({ product, customQuery, customHeaders }) => {
    Logger.debug("useWishlist/removeItem", product);
    try {
      loading.value = true;
      Logger.debug("[Magento Storefront]: useWishlist.removeItem params->", {
        currentWishlist: wishlistStore.wishlist,
        product,
        customQuery,
        customHeaders
      });
      const itemOnWishlist = findItemOnWishlist(wishlistStore.wishlist, product);
      const { data } = await app.context.$vsf.$magento.api.removeProductsFromWishlist({
        id: wishlistStore.wishlist.id || "0",
        items: [itemOnWishlist.id]
      }, customQuery, customHeaders);
      Logger.debug("[Result]:", { data });
      useEvent("wishlist:removeItem", { product });
      error.value.removeItem = null;
      wishlistStore.$patch((state) => {
        var _a2, _b;
        state.wishlist = (_b = (_a2 = data == null ? void 0 : data.WmRemoveProductsFromWishlist) == null ? void 0 : _a2.wishlist) != null ? _b : {};
      });
    } catch (err) {
      error.value.removeItem = err;
      Logger.error("useWishlist/removeItem", err);
    } finally {
      loading.value = false;
    }
  };
  const loadItemsCount = async () => {
    var _a2, _b;
    Logger.debug("useWishlist/wishlistItemsCount");
    const apiState = app.context.$vsf.$magento.config.state;
    let itemsCount = null;
    try {
      loading.value = true;
      error.value.loadItemsCount = null;
      if (apiState.getCustomerToken()) {
        const { data } = await app.context.$vsf.$magento.api.wishlistItemsCount();
        Logger.debug("[Result]:", { data });
        const loadedWishlist = (_b = (_a2 = data == null ? void 0 : data.customer) == null ? void 0 : _a2.wishlists) != null ? _b : [];
        itemsCount = calculateWishlistTotal(loadedWishlist);
        wishlistStore.$patch((state) => {
          state.wishlist.items_count = itemsCount;
        });
      } else {
        const itemsCount2 = await loadGuestWishlistItemsCount();
        if (itemsCount2 !== null) {
          wishlistStore.$patch((state) => {
            state.wishlist.items_count = itemsCount2;
          });
        }
      }
    } catch (err) {
      error.value.loadItemsCount = err;
      Logger.error("useWishlist/wishlistItemsCount", err);
    } finally {
      loading.value = false;
    }
    return itemsCount;
  };
  const addItem = async ({ product, customQuery, customHeaders }) => {
    var _a2, _b, _c, _d;
    Logger.debug("useWishlist/addItem", product);
    try {
      loading.value = true;
      Logger.debug("[Magento Storefront]: useWishlist.addItem params->", {
        currentWishlist: wishlistStore.wishlist,
        product,
        customQuery,
        customHeaders
      });
      if (!wishlistStore.wishlist) {
        await load({});
      }
      const itemOnWishlist = findItemOnWishlist(wishlistStore.wishlist, product);
      if (itemOnWishlist) {
        return;
      }
      switch (product.__typename) {
        case "VirtualProduct":
        case "DownloadableProduct":
        case "GroupedProduct":
        case "GiftCard":
        case "SimpleProduct": {
          const { data } = await app.context.$vsf.$magento.api.addProductToWishList({
            id: wishlistStore.wishlist.id || "0",
            items: [{
              sku: product.sku,
              quantity: 1
            }]
          }, customQuery, customHeaders);
          Logger.debug("[Result]:", { data });
          wishlistStore.$patch((state) => {
            var _a3, _b2;
            state.wishlist = (_b2 = (_a3 = data == null ? void 0 : data.WmAddProductsToWishlist) == null ? void 0 : _a3.wishlist) != null ? _b2 : {};
          });
          useEvent("wishlist:addItem", { product });
          break;
        }
        case "ConfigurableProduct": {
          const { data: configurableProductData } = await app.context.$vsf.$magento.api.addProductToWishList({
            id: wishlistStore.wishlist.id || "0",
            items: [{
              sku: ((_b = (_a2 = product.configurable_product_options_selection) == null ? void 0 : _a2.variant) == null ? void 0 : _b.sku) || product.sku,
              quantity: 1,
              parent_sku: product.sku
            }]
          }, customQuery, customHeaders);
          Logger.debug("[Result]:", { data: configurableProductData });
          wishlistStore.$patch((state) => {
            var _a3, _b2;
            state.wishlist = (_b2 = (_a3 = configurableProductData == null ? void 0 : configurableProductData.WmAddProductsToWishlist) == null ? void 0 : _a3.wishlist) != null ? _b2 : {};
          });
          useEvent("wishlist:addItem", { product: { ...product, sku: ((_d = (_c = product.configurable_product_options_selection) == null ? void 0 : _c.variant) == null ? void 0 : _d.sku) || product.sku } });
          break;
        }
        case "BundleProduct": {
          const { data: bundleProductData } = await app.context.$vsf.$magento.api.addProductToWishList({
            id: wishlistStore.wishlist.id || "0",
            items: [{
              sku: product.sku,
              quantity: 1,
              entered_options: []
            }]
          }, customQuery, customHeaders);
          Logger.debug("[Result]:", { data: bundleProductData });
          wishlistStore.$patch((state) => {
            var _a3, _b2;
            state.wishlist = (_b2 = (_a3 = bundleProductData == null ? void 0 : bundleProductData.WmAddProductsToWishlist) == null ? void 0 : _a3.wishlist) != null ? _b2 : {};
          });
          useEvent("wishlist:addItem", { product });
          break;
        }
        default:
          Logger.error(`Product Type ${product.__typename} not supported in add to wishlist yet`);
      }
    } catch (err) {
      error.value.addItem = err;
      Logger.error("useWishlist/addItem", err);
    } finally {
      loading.value = false;
    }
  };
  const clear = async () => {
    Logger.debug("useWishlist/clear");
    try {
      loading.value = true;
      error.value.clear = null;
      wishlistStore.$patch((state) => {
        state.wishlist = {};
      });
    } catch (err) {
      error.value.clear = err;
      Logger.error("useWishlist/clear", err);
    } finally {
      loading.value = false;
    }
  };
  const afterAddingWishlistItemToCart = ({ product, cartError }) => {
    Logger.debug("useWishlist/afterAddingItemToCart", product);
    if (!isInWishlist({ product }))
      return;
    try {
      if (cartError == null ? void 0 : cartError.message) {
        sendNotification({
          id: Symbol("product_added_to_cart_from_wishlist_error"),
          message: cartError.message,
          type: "danger",
          icon: "cross",
          persist: false,
          title: "Wishlist error"
        });
      } else {
        removeItem({ product }).then(() => {
          sendNotification({
            id: Symbol("product_added_to_cart_from_wishlist"),
            message: app.i18n.t(
              "You added {product} to your shopping cart.",
              { product: product.name }
            ),
            type: "success",
            icon: "check",
            persist: false,
            title: "Wishlist"
          });
        });
      }
    } catch (err) {
      error.value.afterAddingWishlistItemToCart = err;
      Logger.error("useWishlist/afterAddingWishlistItemToCart", err);
    }
  };
  const addOrRemoveItem = async ({ product, customQuery, customHeaders }) => {
    await (isInWishlist({ product }) ? removeItem({ product, customQuery, customHeaders }) : addItem({ product, customQuery, customHeaders }));
  };
  return {
    loadItemsCount,
    isInWishlist,
    addItem,
    load,
    removeItem,
    clear,
    setWishlist,
    afterAddingWishlistItemToCart,
    addOrRemoveItem,
    loading: readonly(loading),
    error: readonly(error),
    enabled
  };
}
var useWishlist_default = useWishlist;
export * from "./useWishlist";
export {
  useWishlist_default as default,
  useWishlist
};
